/**
 * Semelhança morfométrica entre fichas.
 *
 * Cada variável é padronizada (z-score na população de candidatos + alvo) para
 * que um centímetro de cernelha e um grau de ângulo pesem igual. A distância é
 * a euclidiana média sobre as variáveis que os dois têm — média, e não soma,
 * para que uma ficha com menos medidas não pareça mais próxima só por isso.
 */

export type Valores = Readonly<Record<string, number | null | undefined>>;

export interface Candidato<T> {
  item: T;
  valores: Valores;
}

export interface Distancia<T> {
  item: T;
  /** Distância média em desvios-padrão. Menor = mais semelhante. */
  distancia: number;
  /** Quantas variáveis os dois tinham em comum. */
  variaveisUsadas: number;
}

const numero = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v);

function estatisticas(amostra: number[]) {
  const n = amostra.length;
  const media = amostra.reduce((a, b) => a + b, 0) / n;
  const variancia = amostra.reduce((a, b) => a + (b - media) ** 2, 0) / n;
  return { media, dp: Math.sqrt(variancia) };
}

/**
 * Ordena os candidatos do mais semelhante ao mais divergente do alvo.
 * Candidatos sem nenhuma variável em comum com o alvo ficam de fora.
 * Variável sem variação na população (desvio zero) não entra na conta.
 */
export function rankearSemelhantes<T>(
  alvo: Valores,
  candidatos: readonly Candidato<T>[],
  variaveis: readonly string[],
): Distancia<T>[] {
  const escala = new Map<string, { media: number; dp: number }>();
  for (const v of variaveis) {
    const amostra = [alvo[v], ...candidatos.map((c) => c.valores[v])].filter(numero);
    if (amostra.length < 2) continue;
    const e = estatisticas(amostra);
    if (e.dp > 0) escala.set(v, e);
  }

  const resultado: Distancia<T>[] = [];
  for (const c of candidatos) {
    let soma = 0;
    let usadas = 0;
    for (const [v, { dp }] of escala) {
      const a = alvo[v];
      const b = c.valores[v];
      if (!numero(a) || !numero(b)) continue;
      soma += ((a - b) / dp) ** 2;
      usadas += 1;
    }
    if (usadas === 0) continue;
    resultado.push({ item: c.item, distancia: Math.sqrt(soma / usadas), variaveisUsadas: usadas });
  }

  return resultado.sort((x, y) => x.distancia - y.distancia);
}
